"use strict";

var __importDefault = this && this.__importDefault || function (mod) {
  return mod && mod.__esModule ? mod : {
    "default": mod
  };
};
Object.defineProperty(exports, "__esModule", {
  value: true
});
exports.updateProjectProductOptions = void 0;
const uuid_1 = require("uuid");
const either_1 = require("../../types/either");
const Project_1 = require("../../models/Project");
const PrintableGroup_1 = require("../../models/printables/PrintableGroup");
const Product_1 = require("../../models/Product");
const createPrintsPrintableGroups_1 = __importDefault(require("../printables/createPrintsPrintableGroups"));
const removeBooksInteriorPrintables_1 = __importDefault(require("../printables/removeBooksInteriorPrintables"));
const removePrintsPrintableGroups_1 = __importDefault(require("../printables/removePrintsPrintableGroups"));
const createBooksPrintableGroups_1 = require("../printables/createBooksPrintableGroups");
const template_1 = require("../template");
const printables_1 = require("../printables");
const areProductOptionsValidForSku_1 = require("./areProductOptionsValidForSku");
const hasBoxPrintableOption_1 = __importDefault(require("./hasBoxPrintableOption"));
const hasBoxPrintableGroup_1 = __importDefault(require("./hasBoxPrintableGroup"));
const hasDefaultDesign_1 = __importDefault(require("./hasDefaultDesign"));
const createBooksBoxPrintable_1 = __importDefault(require("../printables/createBooksBoxPrintable"));
const getBookInteriorPageCount_1 = __importDefault(require("./getBookInteriorPageCount"));
const isThereARelevantOptionInNameScheme_1 = require("../../helpers/HAMMER/isThereARelevantOptionInNameScheme");
const defaultMissingAndDisabledProductOptions_1 = require("./updateHelpers/defaultMissingAndDisabledProductOptions");
const browser_1 = require("../../helpers/browser");
const Template_1 = require("../../models/Template");
const createCardsPrintableGroups_1 = __importDefault(require("../printables/cards/createCardsPrintableGroups"));
const getNeedsDependsOnUpdate_1 = require("./updateHelpers/getNeedsDependsOnUpdate");
const layers_1 = require("../../helpers/HAMMER/layers");
const updateProjectProductOptions = _ref => {
  let {
    project,
    art,
    timestamp,
    product,
    newOptions,
    isMobile
  } = _ref;
  var _a, _b;
  if (!(0, areProductOptionsValidForSku_1.areProductOptionsValidForSku)(product.options || [], newOptions)) {
    return (0, either_1.left)(new Error(`Product options are invalid for sku ${project.product.sku}`));
  }
  // Account for restricted, dependent, and/or missing options/values
  const combinedOptions = Object.assign(Object.assign({}, project.product.options), newOptions);
  const defaultedNewOptions = (0, defaultMissingAndDisabledProductOptions_1.defaultMissingAndDisabledProductOptions)({
    options: combinedOptions,
    artAttributes: art.attributes,
    artAttributeRestrictions: art.attributeRestrictions
  });
  const cleanOptions = (0, Project_1.calculateDerivedProductOptions)(defaultedNewOptions, project);
  const updatedOptions = Object.keys(cleanOptions).reduce((acc, optionKey) => {
    if (cleanOptions[optionKey] !== project.product.options[optionKey]) {
      acc[optionKey] = cleanOptions[optionKey];
    }
    return acc;
  }, {});
  // Also account for restricted, dependent, and/or missing options/values in old product options
  // So we can select the old template if the name scheme has been updated
  const defaultedOldOptions = (0, defaultMissingAndDisabledProductOptions_1.defaultMissingAndDisabledProductOptions)({
    options: project.product.options,
    artAttributes: art.attributes,
    artAttributeRestrictions: art.attributeRestrictions
  });
  logProductOptions({
    oldOptions: project.product.options,
    defaultedOldOptions,
    cleanOptions
  });
  const needsDependsOnUpdateResult = (0, getNeedsDependsOnUpdate_1.getNeedsDependsOnUpdate)(art, project, cleanOptions);
  if (needsDependsOnUpdateResult.isLeft()) {
    return (0, either_1.left)(needsDependsOnUpdateResult.value);
  }
  const needsDependsOnUpdate = needsDependsOnUpdateResult.value;
  const pageCountOptionName = (0, Product_1.getOptionThatControlsPageCount)(updatedOptions);
  const shouldWeUpdatePrintables = (0, isThereARelevantOptionInNameScheme_1.isThereARelevantOptionInNameScheme)(art.nameScheme, updatedOptions) || !!pageCountOptionName || needsDependsOnUpdate;
  if (shouldWeUpdatePrintables) {
    const selectOldTemplateResult = (0, template_1.selectTemplate)(defaultedOldOptions, art);
    if (selectOldTemplateResult.isLeft()) {
      return (0, either_1.left)(selectOldTemplateResult.value);
    }
    const {
      template: oldTemplate
    } = selectOldTemplateResult.value;
    const selectNewTemplateResult = (0, template_1.selectTemplate)(cleanOptions, art);
    if (selectNewTemplateResult.isLeft()) {
      return (0, either_1.left)(selectNewTemplateResult.value);
    }
    const {
      template: newTemplate,
      templateId: newTemplateId
    } = selectNewTemplateResult.value;
    let updatedProject = project;
    updatedProject.templateId = newTemplateId;
    updatedProject.product.options = cleanOptions;
    const interiorPageCount = pageCountOptionName ? parseInt(updatedProject.product.options[pageCountOptionName]) : (0, getBookInteriorPageCount_1.default)(updatedProject.printableGroups, project.product.hammerCategory);
    updatedProject.product.pageCount = interiorPageCount;
    const targetInteriorPrintableCount = (0, Product_1.isSpreadBooksCategory)(art.category) ? interiorPageCount / 2 : interiorPageCount;
    if ((_a = art.category) === null || _a === void 0 ? void 0 : _a.includes('cards')) {
      if (needsDependsOnUpdate) {
        newTemplate.layers = (0, layers_1.resolveDependsOnValuesForLayers)(newTemplate.layers, art.attributes, cleanOptions);
      }
      updatedProject.printableGroups = (0, createCardsPrintableGroups_1.default)(project.createdBy, project.id, project.createdAt, newTemplate, updatedProject.printableGroups);
    }
    const printableUpdates = updatedProject.printableGroups.map(printableGroup => {
      const updatedPrintables = printableGroup.printables.slice(0, targetInteriorPrintableCount || Infinity).map(printable => {
        return (0, printables_1.updatePrintable)({
          printable: needsDependsOnUpdate ? (0, getNeedsDependsOnUpdate_1.maybeUpdatePrintableDependsOnLayers)(printable, cleanOptions, art.attributes) : printable,
          timestamp,
          designId: cleanOptions.design || printable.designId,
          // sometimes options.design corresponds to a specific layout
          rotateWorkspace: !!printableGroup.rotateWorkspace,
          newTemplate,
          oldTemplate,
          printableGroupType: printableGroup.type,
          interiorPageCount,
          hammerCategory: project.product.hammerCategory,
          ignoreEditableTextTemplateLayers: art.userCanAddText || false
        });
      });
      const {
        lefts: errors,
        rights: printables
      } = (0, either_1.splitArray)(updatedPrintables);
      if (errors.length > 0) {
        return (0, either_1.left)(new Error(errors.map(e => e.message).join('\n')));
      }
      return (0, either_1.right)(Object.assign(Object.assign({}, printableGroup), {
        printables
      }));
    });
    const {
      lefts,
      rights
    } = (0, either_1.splitArray)(printableUpdates);
    if (lefts.length > 0) {
      return (0, either_1.left)(new Error(lefts.map(e => e.message).join('\n')));
    }
    let updatedPrintableGroups = rights;
    const interiorPrintablesGroup = (0, PrintableGroup_1.getInteriorPrintableGroup)(updatedPrintableGroups);
    const numInteriorPrintables = ((_b = interiorPrintablesGroup === null || interiorPrintablesGroup === void 0 ? void 0 : interiorPrintablesGroup.printables) === null || _b === void 0 ? void 0 : _b.length) || 0;
    const oldTemplateMinPrintables = oldTemplate.pages.min;
    const oldTemplateMaxPrintables = oldTemplate.pages.max;
    const newTemplateMinPrintables = newTemplate.pages.min;
    const newTemplateMaxPrintables = newTemplate.pages.max;
    if (oldTemplateMinPrintables < newTemplateMinPrintables || numInteriorPrintables < targetInteriorPrintableCount) {
      let createPrintableGroupsResult;
      if ((0, Product_1.isABooksCategory)(art.category)) {
        createPrintableGroupsResult = (0, createBooksPrintableGroups_1.createBooksPrintableGroups)({
          userId: project.createdBy,
          projectId: project.id,
          creationDate: updatedProject.lastModifiedAt,
          template: newTemplate,
          hammerCategory: art.category,
          currentPrintableGroups: updatedPrintableGroups,
          minimumPageCountOverride: targetInteriorPrintableCount,
          hasBoxPrintableGroup: (0, hasBoxPrintableOption_1.default)(newOptions),
          mobileUserAgentCheck: isMobile,
          hasDefaultDesign: (0, hasDefaultDesign_1.default)({
            template: newTemplate,
            templateId: newTemplateId
          }),
          userCanAddText: art.userCanAddText
        });
      } else if (art.category.includes('prints')) {
        // if there is only 1 design id in use, use that design id to create the new printable, otherwise we pass an empty string so that the template default is used
        const usedDesignIds = Array.from(new Set(project.printableGroups.map(pg => pg.printables.map(p => p.designId)).flat()));
        const newPrintDesignId = usedDesignIds.length === 1 ? usedDesignIds[0] : '';
        createPrintableGroupsResult = (0, createPrintsPrintableGroups_1.default)(project.createdBy, project.id, updatedProject.lastModifiedAt, newTemplate, newPrintDesignId, updatedPrintableGroups);
      } else {
        return (0, either_1.left)(new Error(`Increasing printable / printable group quantity not supported for category ${art.category}`));
      }
      if (createPrintableGroupsResult.isLeft()) {
        return (0, either_1.left)(createPrintableGroupsResult.value);
      }
      updatedPrintableGroups = createPrintableGroupsResult.value;
    } else if (oldTemplateMaxPrintables > newTemplateMaxPrintables) {
      let removePrintableGroupsResult;
      if (art.category.includes('books')) {
        // This should never happen, but just in case... (do any single sku's book templates have different pages.max??)
        // Even if we get here, 'extra' printables have already been sliced out in printableUpdates
        removePrintableGroupsResult = (0, removeBooksInteriorPrintables_1.default)(newTemplate, updatedPrintableGroups, (0, hasBoxPrintableOption_1.default)(newOptions));
      } else if (art.category.includes('prints')) {
        removePrintableGroupsResult = (0, removePrintsPrintableGroups_1.default)(newTemplate, updatedPrintableGroups);
      } else {
        return (0, either_1.left)(new Error(`Removing printable / printable group quantity not supported for category ${art.category}`));
      }
      if (removePrintableGroupsResult.isLeft()) {
        return (0, either_1.left)(removePrintableGroupsResult.value);
      }
      updatedPrintableGroups = removePrintableGroupsResult.value;
    }
    updatedProject.printableGroups = updatedPrintableGroups;
    updatedProject = syncPrintableDesignWithProductOptions(art, updatedProject, updatedPrintableGroups);
    // collect all printables into an array, as all of them have been updated
    const updatedEntities = updatedPrintableGroups.reduce((acc, printableGroup) => [...acc, ...printableGroup.printables], [updatedProject]);
    return (0, either_1.right)({
      updatedProject,
      updatedEntities
    });
  } else {
    const newProject = project;
    newProject.templateId = project.templateId;
    newProject.product.options = cleanOptions;
    let updatedEntities;
    if ((0, Product_1.isABooksCategory)(art.category)) {
      if ((0, hasBoxPrintableOption_1.default)(newOptions) && !(0, hasBoxPrintableGroup_1.default)(newProject.printableGroups)) {
        // If there isn't a wooden box group we'll create a new one.
        const templateResult = (0, template_1.selectTemplate)(newProject.product.options, art);
        if (templateResult.isLeft()) {
          return (0, either_1.left)(templateResult.value);
        }
        if (!(0, Template_1.isTemplateOriginal)(templateResult.value.template)) {
          throw new Error('Error when updating product options: Unsupported template schema version');
        }
        if (templateResult.value.template.woodenBox) {
          const woodenBoxPrintableGroupId = (0, uuid_1.v5)(`Time ${timestamp} Printable Group Box`, newProject.id);
          const printableId = (0, uuid_1.v5)(`Time ${timestamp} Printable Box`, woodenBoxPrintableGroupId);
          const createBooksBoxPrintableResult = (0, createBooksBoxPrintable_1.default)(newProject.lastModifiedBy, newProject.id, timestamp, templateResult.value.template, printableId);
          if (createBooksBoxPrintableResult.isLeft()) {
            return (0, either_1.left)(createBooksBoxPrintableResult.value);
          }
          const woodenBoxGroup = {
            id: woodenBoxPrintableGroupId,
            type: 'wooden-box',
            qty: 1,
            printables: [createBooksBoxPrintableResult.value]
          };
          newProject.printableGroups.push(woodenBoxGroup);
          updatedEntities = [newProject, createBooksBoxPrintableResult.value];
        }
      } else if (!(0, hasBoxPrintableOption_1.default)(newProject.product.options) && (0, hasBoxPrintableGroup_1.default)(newProject.printableGroups)) {
        // If there isn't a wooden box option we'll remove box printable group
        newProject.printableGroups = newProject.printableGroups.filter(pg => pg.type !== 'wooden-box');
      }
      return (0, either_1.right)({
        updatedProject: newProject,
        updatedEntities: updatedEntities || [newProject]
      });
    }
    const updateProjectReturn = {
      updatedProject: newProject,
      updatedEntities: updatedEntities || [newProject]
    };
    return (0, either_1.right)(updateProjectReturn);
  }
};
exports.updateProjectProductOptions = updateProjectProductOptions;
const getUpdatedPrintableWithDesignId = printableGroups => {
  for (const printableGroup of printableGroups) {
    for (const printable of printableGroup.printables) {
      if (printable.designId) {
        return printable;
      }
    }
  }
};
const getARTDesignControllingAttribute = _art => {
  const attributeEntry = Object.entries(_art.attributes).find(entry => entry[1].options.find(option => !!option.designId));
  if (!attributeEntry) {
    return attributeEntry;
  }
  return {
    key: attributeEntry[0],
    attribute: attributeEntry[1]
  };
};
// Changing product options has the chance to update the selected `template.design` (layout) of the project
// If there is an updated printable entity, with a designId, _and_ there is a "design controlling attribute", then
// we need to add that attribute and the new designId to the payload
const syncPrintableDesignWithProductOptions = (art, project, updatedPrintableGroups) => {
  const updatedDesignEntity = getUpdatedPrintableWithDesignId(updatedPrintableGroups);
  if (!updatedDesignEntity) {
    return project;
  }
  const attributeEntry = getARTDesignControllingAttribute(art);
  if (!attributeEntry) {
    return project;
  }
  const optionWithDesignId = attributeEntry.attribute.options.find(option => option.designId === updatedDesignEntity.designId);
  if (!optionWithDesignId) {
    return project;
  }
  // Update the project product options with our new attribute value
  return Object.assign(Object.assign({}, project), {
    product: Object.assign(Object.assign({}, project.product), {
      options: Object.assign(Object.assign({}, project.product.options), {
        [attributeEntry.key]: optionWithDesignId.value
      })
    })
  });
};
const logProductOptions = _ref2 => {
  let {
    oldOptions,
    defaultedOldOptions,
    cleanOptions
  } = _ref2;
  if (!(0, browser_1.isServerSideRendering)()) {
    return;
  }
  try {
    console.log('sdk:updateProjectProductOptions log product options', {
      oldOptions,
      defaultedOldOptions,
      cleanOptions
    });
    const oldOptionKeys = Object.keys(oldOptions);
    const defaultedOldOptionKeys = Object.keys(defaultedOldOptions);
    if (oldOptionKeys.length !== defaultedOldOptionKeys.length || oldOptionKeys.some(key => oldOptions[key] !== defaultedOldOptions[key])) {
      console.log('sdk:updateProjectProductOptions defaulted old options are different than old options', {
        oldOptions,
        defaultedOldOptions
      });
    }
  } catch (error) {
    console.log('sdk:updateProjectProductOptions error comparing defaulted old options and old options');
    console.error(error);
  }
};